// src/layout/Sidebar.tsx
import React from "react";
import { NavLink, useNavigate, useLocation } from "react-router-dom";
import {
  MdDashboard,
  MdPeople,
  MdSchool,
  MdWorkspacePremium,
  MdForum,
  MdInsights,
  MdQuiz,
  MdMenu,
  MdClose,
} from "react-icons/md";
import { useAuthStore } from "../stores/authStore";
import RoleBadge from "../components/RoleBadge";
import type { Role } from "../models/auth";

type Item = { to: string; label: string; icon: React.ReactElement };

/* Catálogo completo de entradas posibles en el sidebar */
const NAV_ITEMS: readonly Item[] = [
  { to: "/", label: "Inicio", icon: <MdDashboard /> },
  { to: "/users", label: "Usuarios", icon: <MdPeople /> },
  { to: "/courses", label: "Cursos", icon: <MdSchool /> },
  { to: "/questionnaires", label: "Cuestionarios", icon: <MdQuiz /> },
  {
    to: "/certifications",
    label: "Certificaciones",
    icon: <MdWorkspacePremium />,
  },
  { to: "/community", label: "Comunidad", icon: <MdForum /> },
  { to: "/stats", label: "Estadísticas", icon: <MdInsights /> },
] as const;

/* Helpers de permisos (solo para el menú lateral) */
const ADMIN_SET = new Set<Role>(["owner", "super_admin", "admin"]);

/** Rutas visibles por rol (Inicio *no* es universal; solo admins lo ven) */
function routesFor(role: Role | null): Set<string> {
  const allowed = new Set<string>();

  if (role && ADMIN_SET.has(role)) {
    for (const it of NAV_ITEMS) allowed.add(it.to);
    return allowed;
  }

  switch (role) {
    case "moderator":
    case "publisher":
      allowed.add("/community");
      return allowed;

    case "instructor":
      allowed.add("/courses");
      return allowed;

    case "auditor":
      allowed.add("/questionnaires");
      allowed.add("/certifications");
      return allowed;

    default:
      return allowed; // sin accesos visibles
  }
}

/* ================= Desktop ================= */

export default function Sidebar(): React.ReactElement {
  const auth = useAuthStore((s) => s.state);
  const navigate = useNavigate();

  const displayName: string =
    auth.profile?.fullName ??
    auth.user?.displayName ??
    auth.user?.email ??
    "Usuario";

  const photoURL: string | null =
    auth.profile?.photoURL ?? auth.user?.photoURL ?? null;

  const role: Role | null = auth.profile?.role ?? null;

  const goUsersWithRole = () => {
    if (role) navigate(`/users?role=${encodeURIComponent(role)}`);
  };

  const allowed = routesFor(role);
  const visibleItems = NAV_ITEMS.filter((it) => allowed.has(it.to));

  return (
    <aside
      className="hidden w-64 flex-col gap-1 border-r border-surface bg-surface/90 p-3 backdrop-blur md:flex"
      role="navigation"
      aria-label="Principal"
      style={{
        backgroundImage:
          "radial-gradient(420px 160px at 100% 0%, color-mix(in oklab, var(--color-primary) 10%, transparent) 0%, transparent 70%), radial-gradient(360px 140px at 0% 100%, color-mix(in oklab, var(--color-secondary) 10%, transparent) 0%, transparent 70%)",
      }}
    >
      <UserCard
        displayName={displayName}
        email={auth.user?.email ?? null}
        photoURL={photoURL}
        role={role}
        onRoleClick={goUsersWithRole}
      />

      {visibleItems.map((item) => (
        <NavItem
          key={item.to}
          to={item.to}
          icon={item.icon}
          label={item.label}
        />
      ))}

      <div className="mt-auto" />
    </aside>
  );
}

/* ================= Mobile Drawer ================= */

export function MobileMenuButton({
  onClick,
}: {
  onClick: () => void;
}): React.ReactElement {
  return (
    <button
      type="button"
      className="inline-flex items-center justify-center rounded-lg p-2 text-on-surface/80 hover:bg-primary/10 focus-visible:bg-primary/10 md:hidden"
      aria-label="Abrir menú"
      onClick={onClick}
    >
      <MdMenu size={22} />
    </button>
  );
}

export function MobileSidebar({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}): React.ReactElement {
  const auth = useAuthStore((s) => s.state);
  const navigate = useNavigate();
  const location = useLocation();

  const displayName: string =
    auth.profile?.fullName ??
    auth.user?.displayName ??
    auth.user?.email ??
    "Usuario";

  const photoURL: string | null =
    auth.profile?.photoURL ?? auth.user?.photoURL ?? null;

  const role: Role | null = auth.profile?.role ?? null;

  const goUsersWithRole = () => {
    if (role) navigate(`/users?role=${encodeURIComponent(role)}`);
  };

  const allowed = routesFor(role);
  const visibleItems = NAV_ITEMS.filter((it) => allowed.has(it.to));

  // Cierra cuando cambia la ruta
  React.useEffect(() => {
    if (open) onClose();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  // Bloquear scroll del body al abrir
  React.useEffect(() => {
    if (open) {
      const prev = document.body.style.overflow;
      document.body.style.overflow = "hidden";
      return () => {
        document.body.style.overflow = prev;
      };
    }
    return;
  }, [open]);

  // Cerrar con ESC
  React.useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  return (
    <div
      aria-hidden={!open}
      className={[
        "fixed inset-0 z-50 md:hidden",
        open ? "pointer-events-auto" : "pointer-events-none",
      ].join(" ")}
    >
      {/* Backdrop */}
      <div
        className={[
          "absolute inset-0 bg-black/40 transition-opacity",
          open ? "opacity-100" : "opacity-0",
        ].join(" ")}
        onClick={onClose}
      />

      {/* Panel */}
      <div
        className={[
          "absolute left-0 top-0 flex h-full w-[280px] flex-col border-r border-surface bg-surface p-3 shadow-2xl transition-transform",
          open ? "translate-x-0" : "-translate-x-full",
        ].join(" ")}
        role="dialog"
        aria-modal="true"
        aria-label="Menú de navegación"
      >
        <div className="mb-2 flex items-center justify-between">
          <div className="min-w-0">
            <UserCard
              displayName={displayName}
              email={auth.user?.email ?? null}
              photoURL={photoURL}
              role={role}
              onRoleClick={goUsersWithRole}
              compact
            />
          </div>
          <button
            className="rounded-lg p-2 text-on-surface/70 hover:bg-primary/10"
            onClick={onClose}
            aria-label="Cerrar menú"
          >
            <MdClose size={20} />
          </button>
        </div>

        <nav className="mt-1 space-y-1">
          {visibleItems.map((item) => (
            <NavItem
              key={item.to}
              to={item.to}
              icon={item.icon}
              label={item.label}
            />
          ))}
        </nav>
      </div>
    </div>
  );
}

/* ================= Partes reutilizables ================= */

function UserCard({
  displayName,
  email,
  photoURL,
  role,
  onRoleClick,
  compact = false,
}: {
  displayName: string;
  email: string | null;
  photoURL: string | null;
  role: Role | null;
  onRoleClick: () => void;
  compact?: boolean;
}): React.ReactElement {
  return (
    <div
      className={[
        "rounded-xl border border-surface bg-surface shadow-soft",
        compact ? "px-2 py-1.5" : "px-3 py-2",
      ].join(" ")}
    >
      <div className="flex items-center gap-2">
        <Avatar
          name={displayName}
          email={email}
          photoURL={photoURL}
          size={32}
        />
        <div className="min-w-0">
          <div className="truncate text-sm font-medium">{displayName}</div>
          <div className="mt-1">
            <button
              type="button"
              onClick={onRoleClick}
              disabled={!role}
              className="disabled:opacity-60"
              title={role ? `Ver usuarios con rol ${role}` : "Sin rol"}
            >
              <RoleBadge role={role ?? null} />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function NavItem({
  to,
  icon,
  label,
}: {
  to: string;
  icon: React.ReactElement;
  label: string;
}): React.ReactElement {
  return (
    <NavLink
      to={to}
      className={({ isActive }) =>
        [
          "group relative flex items-center gap-3 rounded-xl px-3 py-2 text-sm outline-none transition-colors",
          isActive
            ? "text-primary"
            : "hover:bg-primary/10 focus-visible:bg-primary/10",
        ].join(" ")
      }
    >
      {({ isActive }) => (
        <>
          {isActive && (
            <span
              aria-hidden
              className="absolute left-0 top-2 bottom-2 w-1 rounded-full"
              style={{ background: "var(--color-primary)" }}
            />
          )}
          <span
            className={[
              "grid h-8 w-8 place-items-center rounded-lg transition-colors",
              isActive
                ? "text-primary"
                : "text-on-surface/70 group-hover:text-primary",
            ].join(" ")}
            style={{
              background: isActive
                ? "color-mix(in oklab, var(--color-primary) 16%, transparent)"
                : "color-mix(in oklab, var(--color-onSurface) 8%, transparent)",
            }}
          >
            {icon}
          </span>
          <span className="min-w-0 truncate">{label}</span>
        </>
      )}
    </NavLink>
  );
}

/** Avatar redondo: foto o iniciales */
function Avatar({
  name,
  email,
  photoURL,
  size = 28,
}: {
  name: string;
  email: string | null;
  photoURL: string | null;
  size?: number;
}): React.ReactElement {
  const initials = getInitials(name, email);
  const px = `${size}px`;

  if (photoURL && photoURL.length > 0) {
    return (
      <img
        src={photoURL}
        alt={name}
        width={size}
        height={size}
        className="rounded-full object-cover"
        style={{ width: px, height: px }}
      />
    );
  }
  return (
    <div
      aria-hidden
      className="grid place-items-center rounded-full bg-primary/20 text-primary"
      style={{
        width: px,
        height: px,
        fontSize: Math.max(10, Math.floor(size * 0.45)),
      }}
      title={name}
    >
      {initials}
    </div>
  );
}

function getInitials(name: string, email: string | null): string {
  const n = (name ?? "").trim();
  if (n.length > 0) {
    const parts = n.split(/\s+/);
    const a = parts[0]?.[0] ?? "";
    const b = parts[parts.length - 1]?.[0] ?? "";
    const initials = `${a}${b || ""}`.toUpperCase();
    return initials || "U";
  }
  const mail = (email ?? "").trim();
  if (mail.includes("@")) return mail[0]!.toUpperCase();
  return "U";
}
